import React, { useState, useRef, useEffect } from 'react';
import { MoleculeIcon, ReactionIcon, ProteinIcon, DataFileIcon } from '../icons/ContentIcons';
import { formatRelativeDate } from '../../utils/dateFormat';
import './ProjectCard.css';

export interface ContentCounts {
  molecules: number;
  reactions: number;
  proteins: number;
  files: number;
}

interface ProjectCardProps {
  id: number;
  name: string;
  description?: string;
  color: string;
  contentCounts?: ContentCounts;
  lastModified?: string;
  patentRiskCount?: number;
  archived?: boolean;
  onClick: () => void;
  onRename?: (id: number, newName: string) => Promise<void>;
  onArchive?: (id: number) => Promise<void>;
  onDelete?: () => void;
  onRestore?: () => void;
  onPermanentDelete?: () => void;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  name,
  description,
  color,
  contentCounts,
  lastModified,
  patentRiskCount,
  archived,
  onClick,
  onRename,
  onArchive,
  onDelete,
  onRestore,
  onPermanentDelete,
}) => {
  const counts = contentCounts || { molecules: 0, reactions: 0, proteins: 0, files: 0 };
  const risk = patentRiskCount ?? 0;

  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(name);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Focus input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameSubmit = async () => {
    if (renameValue.trim() && renameValue !== name && onRename) {
      await onRename(id, renameValue.trim());
    }
    setIsRenaming(false);
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const hasMenu = onRename || onArchive || onDelete || onRestore || onPermanentDelete;

  return (
    <>
      <button
        className={`project-card${archived ? ' project-card--archived' : ''}`}
        onClick={onClick}
        style={archived ? { opacity: 0.65, filter: 'saturate(0.4)' } : undefined}
      >
        <div className="project-card__accent" style={{ backgroundColor: color }} />

        <div
          className="project-card__icon"
          style={{ backgroundColor: `${color}15` }}
        >
          <svg style={{ color }} fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z" />
          </svg>
        </div>

        <div className="project-card__name-row">
          <h3 className="project-card__name">{name}</h3>
          {name.startsWith('Sample:') && (
            <span className="project-card__badge project-card__badge--demo">DEMO</span>
          )}
          {archived && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              padding: '1px 5px', borderRadius: 3,
              background: '#f59e0b20', color: '#d97706',
              textTransform: 'uppercase',
            }}>
              Archived
            </span>
          )}
          {!archived && (patentRiskCount ?? 0) > 0 && (
            <span className="project-card__badge project-card__badge--patent" title={`${patentRiskCount} molecules with patent hits`}>
              <span className="project-card__patent-dot" />
              {patentRiskCount}
            </span>
          )}
        </div>

        {description && (
          <p className="project-card__description">{description}</p>
        )}

        {/* Stats grid */}
        <div className="project-card__stats">
          <div className="project-card__stat">
            <span className="project-card__stat-value">{formatCount(counts.molecules)}</span>
            <span className="project-card__stat-label">
              <MoleculeIcon size={10} />
              COMPOUNDS
            </span>
          </div>
          <div className="project-card__stat">
            <span className="project-card__stat-value">{formatCount(counts.files)}</span>
            <span className="project-card__stat-label">
              <DataFileIcon size={10} />
              FILES
            </span>
          </div>
          <div className="project-card__stat">
            <span className="project-card__stat-value" style={{ color: counts.reactions > 0 ? '#0D9488' : undefined }}>{formatCount(counts.reactions)}</span>
            <span className="project-card__stat-label">
              <ReactionIcon size={10} />
              REACTIONS
            </span>
          </div>
          <div className="project-card__stat">
            <span className={`project-card__stat-value${risk > 10 ? ' project-card__stat-value--amber' : ''}`}>
              {risk > 0 ? risk : '\u2014'}
            </span>
            <span className="project-card__stat-label">
              <ProteinIcon size={10} />
              PATENT HITS
            </span>
          </div>
          <div className="project-card__stat">
            <span className="project-card__stat-value">{'\u2014'}</span>
            <span className="project-card__stat-label">
              <ProteinIcon size={10} />
              PAT. FILED
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="project-card__footer">
          {archived ? (
            /* Archived card: show Restore / Delete buttons */
            <div className="flex gap-2 w-full" onClick={(e) => e.stopPropagation()}>
              {onRestore && (
                <button
                  onClick={onRestore}
                  className="flex-1 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                >
                  Restore
                </button>
              )}
              {onPermanentDelete && (
                <button
                  onClick={onPermanentDelete}
                  className="flex-1 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                >
                  Delete Forever
                </button>
              )}
            </div>
          ) : (
            /* Active card: show patent zones + timestamp */
            <>
              <div className="project-card__patent-zones">
                {risk > 0 ? (
                  <span className="project-card__zone-summary">
                    <span className="project-card__zone-dot" />
                    <span className="project-card__zone-count">{risk} patent hit{risk !== 1 ? 's' : ''}</span>
                  </span>
                ) : (
                  <span className="project-card__zone-none">No patent hits</span>
                )}
              </div>
              <span className="project-card__timestamp">{formatRelativeDate(lastModified)}</span>
            </>
          )}
        </div>

        {/* Menu button — active cards only */}
        {hasMenu && !archived && (
          <div
            onClick={handleMenuClick}
            className="project-card__menu-btn"
            role="button"
            tabIndex={0}
          >
            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </div>
        )}

        {/* Dropdown menu */}
        {showMenu && !archived && (
          <div
            ref={menuRef}
            className="project-card__menu"
            onClick={(e) => e.stopPropagation()}
          >
            {onRename && (
              <button
                onClick={() => {
                  setShowMenu(false);
                  setRenameValue(name);
                  setIsRenaming(true);
                }}
                className="project-card__menu-item"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Rename
              </button>
            )}
            {onRename && onArchive && <div className="project-card__menu-separator" />}
            {onArchive && (
              <button
                onClick={() => {
                  setShowMenu(false);
                  if (window.confirm(`Archive "${name}"? It will be moved to Archived Projects.`)) {
                    onArchive(id);
                  }
                }}
                className="project-card__menu-item"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Archive
              </button>
            )}
            {onDelete && (onRename || onArchive) && <div className="project-card__menu-separator" />}
            {onDelete && (
              <button
                onClick={() => {
                  setShowMenu(false);
                  onDelete();
                }}
                className="project-card__menu-item project-card__menu-item--danger"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            )}
          </div>
        )}
      </button>

      {/* Rename modal */}
      {isRenaming && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setIsRenaming(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl p-6 w-96 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Rename Project</h3>
            <input
              ref={inputRef}
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
              placeholder="Project name"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setIsRenaming(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={!renameValue.trim() || renameValue === name}
                className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectCard;
